import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import './ApiDocsPage.css';
import { useTranslation } from 'react-i18next';

// 定义API响应类型
interface AppDetailResponse {
  app_id: number;
  app_name: string;
  description: string;
  publish_uuid?: string;
  published_at?: string;
  [key: string]: any;
}

const ApiDocsPage: React.FC = () => {
  const { t } = useTranslation();
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workspaceId = searchParams.get('workspace_id');
  
  // 添加日志以便调试
  console.log('API文档页面参数:', { appId, workspaceId, currentUrl: window.location.href });
  
  const [publishUuid, setPublishUuid] = useState<string>('');
  const [appName, setAppName] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('conversation');
  const [activeCodeTab, setActiveCodeTab] = useState<string>('curl');
  const [copied, setCopied] = useState<{[key: string]: boolean}>({});
  const [loading, setLoading] = useState<boolean>(true);
  
  // 检查是否缺少appId参数
  useEffect(() => {
    if (!appId) {
      console.log('没有找到appId参数，显示文档中心页面');
      setLoading(false);
      return;
    }
    
    const fetchAppDetails = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await axios.get<AppDetailResponse>(
          `https://ceshi.lightark.cc/api/v1/apps/${appId}/detail`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              workspace_id: workspaceId
            }
          }
        );

        if (response.data && response.data.publish_uuid) {
          setPublishUuid(response.data.publish_uuid);
          setAppName(response.data.app_name || t('apps.detail.app'));
        } else {
          // 如果应用未发布，则无法查看API文档
          navigate(`/apps/detail/${appId}?workspace_id=${workspaceId || ''}`);
        }
      } catch (error) {
        console.error('获取应用详情失败:', error);
        navigate(`/apps/detail/${appId}?workspace_id=${workspaceId || ''}`);
      } finally {
        setLoading(false);
      }
    };

    fetchAppDetails();
  }, [appId, navigate, workspaceId, t]);

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied({...copied, [key]: true});
      setTimeout(() => {
        setCopied({...copied, [key]: false});
      }, 2000);
    });
  };

  // 返回应用详情页面
  const handleGoBack = () => {
    // 简化逻辑，直接使用字符串拼接方式
    if (workspaceId) {
      navigate(`/apps/detail/${appId}?workspace_id=${workspaceId}`);
    } else {
      navigate(`/apps/detail/${appId}`);
    }
  };
  
  // 文件上传curl示例
  const fileUploadCurl = `curl --location 'https://ceshi.lightark.cc/api/v1/files/upload/temp' \\
--form 'file=@"/path/to/your/file.txt"'`;

  // 对话接口curl示例
  const conversationCurl = `curl --location 'https://ceshi.lightark.cc/api/v1/flow/run-flow-api' \\
--header 'Accept-Language: zh' \\
--header 'Authorization: Bearer app-${publishUuid}' \\
--header 'Content-Type: application/json' \\
--data '{
  "task_input": "hellow",
  "response_mode": "blocking",
  "file_ids": ["5b4ae9b4-51b4-4769-9faa-76a6061ab946"]
}'`;

  // 对话接口Python示例
  const conversationPython = `import requests

url = "https://ceshi.lightark.cc/api/v1/flow/run-flow-api"

payload = {
    "task_input": "hellow",
    "response_mode": "blocking",
    "file_ids": ["5b4ae9b4-51b4-4769-9faa-76a6061ab946"]
}
headers = {
    "Accept-Language": "zh",
    "Authorization": "Bearer app-${publishUuid}",
    "Content-Type": "application/json"
}

response = requests.request("POST", url, json=payload, headers=headers)

print(response.text)`;

  // 文件上传Python示例
  const fileUploadPython = `import requests

url = "https://ceshi.lightark.cc/api/v1/files/upload/temp"

files = {
    'file': open('/path/to/your/file.txt', 'rb')
}

response = requests.post(url, files=files)
print(response.text)`;

  if (loading) {
    return (
      <div className="api-docs-loading">
        <div className="loading-spinner"></div>
        <p>{t('apiDocs.loading')}</p>
      </div>
    );
  }
  
  // 处理缺少appId的情况，显示文档中心页面
  if (!appId) {
    return (
      <div className="docs-center-container">
        <Header />
        <div className="docs-center-content">
          <div className="docs-center-header">
            <h1>{t('apiDocs.docsCenter.title')}</h1>
            <p>{t('apiDocs.docsCenter.welcome')}</p>
          </div>
          
          <div className="docs-navigation">
            <Link to="/" className="nav-card">
              <div className="nav-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8h5z" fill="currentColor"/>
                </svg>
              </div>
              <div className="nav-content">
                <h3>{t('apiDocs.docsCenter.goHome')}</h3>
                <p>{t('apiDocs.docsCenter.goHomeDesc')}</p>
              </div>
            </Link>
            
            <Link to="/apps" className="nav-card">
              <div className="nav-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z" fill="currentColor"/>
                </svg>
              </div>
              <div className="nav-content">
                <h3>{t('apiDocs.docsCenter.appList')}</h3>
                <p>{t('apiDocs.docsCenter.appListDesc')}</p>
              </div>
            </Link>
          </div>
          
          <div className="docs-section">
            <h2>{t('apiDocs.docsCenter.apiDocs')}</h2>
            <p>{t('apiDocs.docsCenter.apiDocsDesc')}</p>
          </div>
          
          <div className="docs-section">
            <h2>{t('apiDocs.docsCenter.moreResources')}</h2>
            <p>{t('apiDocs.docsCenter.moreResourcesDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="api-docs-container">
      <Header />
      <div className="api-docs-wrapper">
        <div className="api-docs-sidebar">
          <div className="sidebar-header">
            <h2>{t('apiDocs.appApi.title', { appName })}</h2>
            <button className="back-button" onClick={handleGoBack}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z" fill="currentColor"/>
              </svg>
              {t('apiDocs.appApi.backToApp')}
            </button>
          </div>
          <div className="sidebar-content">
            <ul className="sidebar-nav">
              <li 
                className={`sidebar-nav-item ${activeTab === 'conversation' ? 'active' : ''}`}
                onClick={() => setActiveTab('conversation')}
              >
                <div className="nav-item-content">
                  <span className="method-badge post">POST</span>
                  <span className="endpoint-path">/flow/run-flow-api</span>
                </div>
                <p className="endpoint-description">{t('apiDocs.appApi.conversationApi')}</p>
              </li>
              <li 
                className={`sidebar-nav-item ${activeTab === 'file-upload' ? 'active' : ''}`}
                onClick={() => setActiveTab('file-upload')}
              >
                <div className="nav-item-content">
                  <span className="method-badge post">POST</span>
                  <span className="endpoint-path">/files/upload/temp</span>
                </div>
                <p className="endpoint-description">{t('apiDocs.appApi.fileUploadApi')}</p>
              </li>
              <li 
                className={`sidebar-nav-item ${activeTab === 'authentication' ? 'active' : ''}`}
                onClick={() => setActiveTab('authentication')}
              >
                <div className="nav-item-content">
                  <span className="endpoint-path">{t('apiDocs.appApi.authentication')}</span>
                </div>
                <p className="endpoint-description">{t('apiDocs.appApi.apiAuthMethod')}</p>
              </li>
            </ul>
          </div>
          
          <div className="api-key-section">
            <h3>{t('apiDocs.appApi.apiKey.title')}</h3>
            <div className="api-key">
              <code>app-{publishUuid}</code>
              <button 
                className="copy-btn"
                onClick={() => copyToClipboard(`app-${publishUuid}`, 'api-key')}
              >
                {copied['api-key'] ? t('apiDocs.appApi.copied') : t('apiDocs.appApi.copy')}
              </button>
            </div>
          </div>
        </div>
        
        <div className="api-docs-content">
          {activeTab === 'conversation' && (
            <div className="endpoint-section">
              <div className="endpoint-header">
                <h1>
                  <span className="method-badge post">POST</span>
                  <span className="endpoint-url">/api/v1/flow/run-flow-api</span>
                </h1>
                <p className="endpoint-description">
                  {t('apiDocs.appApi.conversation.title')}
                </p>
              </div>
              
              <div className="endpoint-detail">
                <h2>{t('apiDocs.appApi.body')}</h2>
                <p>{t('apiDocs.appApi.conversation.description')}</p>
                
                <table className="parameters-table">
                  <thead>
                    <tr>
                      <th>{t('apiDocs.appApi.paramName')}</th>
                      <th>{t('apiDocs.appApi.paramType')}</th>
                      <th>{t('apiDocs.appApi.required')}</th>
                      <th>{t('apiDocs.appApi.paramDesc')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>task_input</td>
                      <td>string</td>
                      <td>{t('apiDocs.appApi.yes')}</td>
                      <td>{t('apiDocs.appApi.conversation.task_input')}</td>
                    </tr>
                    <tr>
                      <td>response_mode</td>
                      <td>string</td>
                      <td>{t('apiDocs.appApi.yes')}</td>
                      <td>{t('apiDocs.appApi.conversation.response_mode')}</td>
                    </tr>
                    <tr>
                      <td>file_ids</td>
                      <td>array</td>
                      <td>{t('apiDocs.appApi.no')}</td>
                      <td>{t('apiDocs.appApi.conversation.file_ids')}</td>
                    </tr>
                  </tbody>
                </table>
                
                <h2>{t('apiDocs.appApi.response')}</h2>
                <p>{t('apiDocs.appApi.responseDesc')}</p>
                
                <div className="response-section">
                  <div className="code-header">
                    <span>JSON</span>
                    <button 
                      className="copy-btn"
                      onClick={() => copyToClipboard(`{
  "record_id": 535,
  "status": "success",
  "final_result": {
    "content": "响应内容...",
    "timestamp": "2025-05-22T17:43:52.171635"
  }
}`, 'response-json')}
                    >
                      {copied['response-json'] ? t('apiDocs.appApi.copied') : t('apiDocs.appApi.copy')}
                    </button>
                  </div>
                  <pre className="response-code">
{`{
  "record_id": 535,
  "status": "success",
  "final_result": {
    "content": "响应内容...",
    "timestamp": "2025-05-22T17:43:52.171635"
  }
}`}
                  </pre>
                </div>
                
                <h2>{t('apiDocs.appApi.example')}</h2>
                <div className="code-tabs">
                  <div 
                    className={`code-tab ${activeCodeTab === 'curl' ? 'active' : ''}`} 
                    onClick={() => setActiveCodeTab('curl')}
                  >
                    CURL
                  </div>
                  <div 
                    className={`code-tab ${activeCodeTab === 'python' ? 'active' : ''}`}
                    onClick={() => setActiveCodeTab('python')}
                  >
                    PYTHON
                  </div>
                </div>
                
                {activeCodeTab === 'curl' ? (
                  <div className="code-section">
                    <div className="code-header">
                      <span>CURL</span>
                      <button 
                        className="copy-btn"
                        onClick={() => copyToClipboard(conversationCurl, 'curl-code')}
                      >
                        {copied['curl-code'] ? t('apiDocs.appApi.copied') : t('apiDocs.appApi.copy')}
                      </button>
                    </div>
                    <pre className="code-content">
                      {conversationCurl}
                    </pre>
                  </div>
                ) : (
                  <div className="code-section">
                    <div className="code-header">
                      <span>PYTHON</span>
                      <button 
                        className="copy-btn"
                        onClick={() => copyToClipboard(conversationPython, 'python-code')}
                      >
                        {copied['python-code'] ? t('apiDocs.appApi.copied') : t('apiDocs.appApi.copy')}
                      </button>
                    </div>
                    <pre className="code-content">
                      {conversationPython}
                    </pre>
                  </div>
                )}
                
                <h2>{t('apiDocs.appApi.errors.title')}</h2>
                <table className="parameters-table">
                  <thead>
                    <tr>
                      <th>{t('apiDocs.appApi.errors.code')}</th>
                      <th>{t('apiDocs.appApi.paramDesc')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>400</td>
                      <td>{t('apiDocs.appApi.errors.e400')}</td>
                    </tr>
                    <tr>
                      <td>401</td>
                      <td>{t('apiDocs.appApi.errors.e401')}</td>
                    </tr>
                    <tr>
                      <td>404</td>
                      <td>{t('apiDocs.appApi.errors.e404')}</td>
                    </tr>
                    <tr>
                      <td>500</td>
                      <td>{t('apiDocs.appApi.errors.e500')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {activeTab === 'file-upload' && (
            <div className="endpoint-section">
              <div className="endpoint-header">
                <h1>
                  <span className="method-badge post">POST</span>
                  <span className="endpoint-url">/api/v1/files/upload/temp</span>
                </h1>
                <p className="endpoint-description">
                  {t('apiDocs.appApi.fileUpload.title')}
                </p>
              </div>
              
              <div className="endpoint-detail">
                <h2>{t('apiDocs.appApi.body')}</h2>
                <p>{t('apiDocs.appApi.fileUpload.description')}</p>
                
                <table className="parameters-table">
                  <thead>
                    <tr>
                      <th>{t('apiDocs.appApi.paramName')}</th>
                      <th>{t('apiDocs.appApi.paramType')}</th>
                      <th>{t('apiDocs.appApi.required')}</th>
                      <th>{t('apiDocs.appApi.paramDesc')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>file</td>
                      <td>file</td>
                      <td>{t('apiDocs.appApi.yes')}</td>
                      <td>{t('apiDocs.appApi.fileUpload.file')}</td>
                    </tr>
                  </tbody>
                </table>
                
                <h2>{t('apiDocs.appApi.response')}</h2>
                <p>{t('apiDocs.appApi.responseDesc')}</p>
                
                <div className="response-section">
                  <div className="code-header">
                    <span>JSON</span>
                    <button 
                      className="copy-btn"
                      onClick={() => copyToClipboard(`{
    "temp_file_id": "b0f8d420-a1fd-4c9d-97e7-6741ad8b4913",
    "minio_path": "/temp/b0f8d420-a1fd-4c9d-97e7-6741ad8b4913.txt"
}`, 'upload-json')}
                    >
                      {copied['upload-json'] ? t('apiDocs.appApi.copied') : t('apiDocs.appApi.copy')}
                    </button>
                  </div>
                  <pre className="response-code">
{`{
    "temp_file_id": "b0f8d420-a1fd-4c9d-97e7-6741ad8b4913",
    "minio_path": "/temp/b0f8d420-a1fd-4c9d-97e7-6741ad8b4913.txt"
}`}
                  </pre>
                </div>
                
                <h2>{t('apiDocs.appApi.example')}</h2>
                <div className="code-tabs">
                  <div 
                    className={`code-tab ${activeCodeTab === 'curl' ? 'active' : ''}`} 
                    onClick={() => setActiveCodeTab('curl')}
                  >
                    CURL
                  </div>
                  <div 
                    className={`code-tab ${activeCodeTab === 'python' ? 'active' : ''}`}
                    onClick={() => setActiveCodeTab('python')}
                  >
                    PYTHON
                  </div>
                </div>
                
                {activeCodeTab === 'curl' ? (
                  <div className="code-section">
                    <div className="code-header">
                      <span>CURL</span>
                      <button 
                        className="copy-btn"
                        onClick={() => copyToClipboard(fileUploadCurl, 'upload-curl')}
                      >
                        {copied['upload-curl'] ? t('apiDocs.appApi.copied') : t('apiDocs.appApi.copy')}
                      </button>
                    </div>
                    <pre className="code-content">
                      {fileUploadCurl}
                    </pre>
                  </div>
                ) : (
                  <div className="code-section">
                    <div className="code-header">
                      <span>PYTHON</span>
                      <button 
                        className="copy-btn"
                        onClick={() => copyToClipboard(fileUploadPython, 'upload-python')}
                      >
                        {copied['upload-python'] ? t('apiDocs.appApi.copied') : t('apiDocs.appApi.copy')}
                      </button>
                    </div>
                    <pre className="code-content">
                      {fileUploadPython}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {activeTab === 'authentication' && (
            <div className="endpoint-section">
              <div className="endpoint-header">
                <h1>{t('apiDocs.appApi.auth.title')}</h1>
                <p className="endpoint-description">
                  {t('apiDocs.appApi.auth.description')}
                </p>
              </div>
              
              <div className="endpoint-detail">
                <h2>{t('apiDocs.appApi.auth.method')}</h2>
                <p>{t('apiDocs.appApi.auth.methodDesc')}</p>
                
                <table className="parameters-table">
                  <thead>
                    <tr>
                      <th>{t('apiDocs.appApi.headerName')}</th>
                      <th>{t('apiDocs.appApi.auth.format')}</th>
                      <th>{t('apiDocs.appApi.required')}</th>
                      <th>{t('apiDocs.appApi.example')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Authorization</td>
                      <td>Bearer {'{your-api-key}'}</td>
                      <td>{t('apiDocs.appApi.yes')}</td>
                      <td>Bearer app-{publishUuid}</td>
                    </tr>
                  </tbody>
                </table>
                
                <div className="auth-example">
                  <h2>{t('apiDocs.appApi.example')}</h2>
                  <div className="code-section">
                    <div className="code-header">
                      <span>Header</span>
                      <button 
                        className="copy-btn"
                        onClick={() => copyToClipboard(`Authorization: Bearer app-${publishUuid}`, 'auth-header')}
                      >
                        {copied['auth-header'] ? t('apiDocs.appApi.copied') : t('apiDocs.appApi.copy')}
                      </button>
                    </div>
                    <pre className="code-content">
{`Authorization: Bearer app-${publishUuid}`}
                    </pre>
                  </div>
                  
                  <div className="auth-note">
                    <h2>{t('apiDocs.appApi.auth.security.title')}</h2>
                    <p>
                      {t('apiDocs.appApi.auth.security.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiDocsPage; 